import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { AgentFormData } from '../components/modals/CreateAgentModal'
import api from '../utils/api'
import { isDemoModeEnabled } from '../utils/demoMode'
import { useAuth } from './AuthContext'

export interface CustomerAgent {
  id: string
  name: string
  description: string
  status: 'active' | 'inactive' | 'training'
  agent_type: 'voice_agent' | 'text_chatbot' | 'custom_llm' | 'document_ai' | 'api_integration'
  conversations: number
  lastActive: string
  businessType: string
  voiceModel: string
  widgets: number
  successRate: number
  llmProvider: 'openai' | 'groq' | 'anthropic' | 'custom'
  systemPrompt: string
  vibeResponse: string
  leads: {
    email: boolean
    name: boolean
    phone: boolean
    company?: boolean
    role?: boolean
    useCase?: boolean
    timing?: boolean
    requestedFollowUp?: boolean
    requireConsent?: boolean
    trigger?: 'always' | 'buying_or_demo_intent'
  }
  discover: {
    public: boolean
    username: string
    title: string
    description: string
  }
}

export interface WidgetCustomization {
  welcomeMessage?: string
  greetingMessage?: string
  primaryColor?: string
  placeholder?: string
  visualizer?: {
    enabled?: boolean
    type?: string
    theme?: string
    size?: string
    showInChat?: boolean
    showInBubble?: boolean
  }
  channels?: {
    text?: boolean
    voice?: boolean
    defaultMode?: 'text' | 'voice'
    default_mode?: 'text' | 'voice'
  }
  voiceExperience?: {
    interactionMode?: 'hands_free' | 'push_to_talk'
    interaction_mode?: 'hands_free' | 'push_to_talk'
    bargeIn?: boolean
    barge_in?: boolean
  }
  websiteForm?: {
    enabled?: boolean
    type?: 'contact' | 'beta' | 'custom'
    title?: string
    description?: string
    submitLabel?: string
    successMessage?: string
    requireConsent?: boolean
    trigger?: 'always' | 'buying_or_demo_intent'
    fields?: Array<{ name: string; label: string; type: 'text' | 'email' | 'tel' | 'textarea'; required?: boolean }>
  }
  [key: string]: unknown
}

export interface CustomerWidget {
  id: string
  agentId: string
  widgetKey?: string
  apiKey?: string
  customerId?: string
  customization?: WidgetCustomization
  name: string
  description: string
  status: 'active' | 'inactive' | 'pending'
  domain: string
  deploymentDate: string
  visitors: number
  interactions: number
  leads?: number
  appointments?: number
  conversionRate: number
  salsaBoard?: {
    syncedEvents: number
    failedEvents: number
    lastSyncedAt: string | null
  }
  deviceBreakdown: {
    desktop: number
    mobile: number
    tablet: number
  }
  lastUpdated: string
  version: string
}

interface CustomerJourneyState {
  agents: CustomerAgent[]
  widgets: CustomerWidget[]
  selectedAgentId: string | null
  summary: CustomerJourneySummary
}

export interface CustomerJourneySummary {
  agents: number
  deployments: number
  liveWidgets: number
  visitors: number
  interactions: number
  leads: number
  appointments: number
  conversations: number
  avgConversion: number
  salsaBoardSynced: number
  salsaBoardFailed: number
}

interface CustomerJourneyContextType {
  agents: CustomerAgent[]
  widgets: CustomerWidget[]
  summary: CustomerJourneySummary
  selectedAgent: CustomerAgent | null
  selectedAgentId: string | null
  selectedWidgets: CustomerWidget[]
  selectAgent: (agentId: string) => void
  createAgent: (agentData: AgentFormData) => Promise<CustomerAgent>
  updateAgent: (agentId: string, updates: Partial<CustomerAgent>) => Promise<void>
  deleteAgent: (agentId: string) => Promise<void>
  ensureWidgetForAgent: (agentId: string, domain?: string) => Promise<CustomerWidget>
  updateWidget: (widgetId: string, updates: Partial<CustomerWidget>) => Promise<CustomerWidget>
  getInstallScript: (widgetId: string) => string
}

const STORAGE_KEY = 'onzoe-customer-journey-v1'
const SALSA_PILOT_DOMAIN = 'salsas.app'
const SALSA_PILOT_WIDGET_KEY = 'salsa-mktg-01868eca8618'

const seedAgents: CustomerAgent[] = [
  {
    id: 'agent_support',
    name: 'Storefront Sales Associate',
    description: 'Website voice agent for product questions, shipping concerns, and first-touch lead capture.',
    status: 'active',
    agent_type: 'voice_agent',
    conversations: 42,
    lastActive: '2 minutes ago',
    businessType: 'E-commerce',
    voiceModel: 'Premium Neural Voice',
    widgets: 1,
    successRate: 0,
    llmProvider: 'openai',
    systemPrompt: 'Answer product, shipping, and pricing questions in a short, helpful voice. Capture email when the buyer is interested but not ready to purchase yet.',
    vibeResponse: 'friendly',
    leads: { email: true, name: true, phone: false },
    discover: {
      public: false,
      username: '',
      title: '',
      description: '',
    },
  },
  {
    id: 'agent_sales',
    name: 'Demo Booking Agent',
    description: 'Voice agent for pricing questions, lightweight qualification, and demo-request follow-up.',
    status: 'active',
    agent_type: 'voice_agent',
    conversations: 31,
    lastActive: '5 minutes ago',
    businessType: 'SaaS',
    voiceModel: 'Premium Neural Voice',
    widgets: 1,
    successRate: 0,
    llmProvider: 'openai',
    systemPrompt: 'Qualify buyers quickly, ask budget and use case questions, and push qualified leads toward a booking or demo request.',
    vibeResponse: 'friendly',
    leads: { email: true, name: true, phone: true },
    discover: {
      public: true,
      username: 'demo-booking-agent',
      title: 'Demo Booking Agent',
      description: 'Answers pricing questions and captures contact details for follow-up.',
    },
  },
  {
    id: 'agent_docs',
    name: 'Service Intake Agent',
    description: 'Voice agent for intake questions, service area checks, and collecting lead details before human follow-up.',
    status: 'training',
    agent_type: 'voice_agent',
    conversations: 8,
    lastActive: '2 hours ago',
    businessType: 'Professional Services',
    voiceModel: 'Premium Neural Voice',
    widgets: 0,
    successRate: 0,
    llmProvider: 'anthropic',
    systemPrompt: 'Ask the visitor what service they need, confirm location and urgency, and collect contact details for a clean follow-up.',
    vibeResponse: 'corporate',
    leads: { email: true, name: true, phone: true },
    discover: {
      public: false,
      username: '',
      title: '',
      description: '',
    },
  },
]

const seedWidgets: CustomerWidget[] = [
  {
    id: 'widget_support_store',
    agentId: 'agent_support',
    name: 'Salsa Marketing Widget',
    description: 'Voice lead-capture widget for the Salsa marketing site.',
    status: 'active',
    widgetKey: SALSA_PILOT_WIDGET_KEY,
    domain: SALSA_PILOT_DOMAIN,
    customization: {
      welcomeMessage: 'Hi! How can I help you today?',
      primaryColor: '#3B82F6',
      visualizer: {
        enabled: true,
        type: 'waveform',
        theme: 'blue',
        size: 'medium',
        showInChat: true,
        showInBubble: false,
      },
    },
    deploymentDate: '2026-04-01',
    visitors: 238,
    interactions: 41,
    conversionRate: 3.4,
    deviceBreakdown: { desktop: 45, mobile: 42, tablet: 13 },
    lastUpdated: '2 hours ago',
    version: 'beta',
    leads: 8,
  },
  {
    id: 'widget_sales_lp',
    agentId: 'agent_sales',
    name: 'Demo Landing Widget',
    description: 'Voice lead-gen widget for the pricing and demo landing page.',
    status: 'active',
    widgetKey: 'widget-sales-lp-demo',
    domain: 'sales.example.com',
    customization: {
      welcomeMessage: 'Hi! How can I help you today?',
      primaryColor: '#10B981',
      visualizer: {
        enabled: true,
        type: 'matrix',
        theme: 'green',
        size: 'medium',
        showInChat: true,
        showInBubble: false,
      },
    },
    deploymentDate: '2026-04-08',
    visitors: 184,
    interactions: 29,
    conversionRate: 4.3,
    deviceBreakdown: { desktop: 52, mobile: 38, tablet: 10 },
    lastUpdated: '30 minutes ago',
    version: 'beta',
    leads: 8,
  },
]

const seedSummary: CustomerJourneySummary = {
  agents: seedAgents.length,
  deployments: seedWidgets.length,
  liveWidgets: seedWidgets.filter((widget) => widget.status === 'active').length,
  visitors: seedWidgets.reduce((sum, widget) => sum + widget.visitors, 0),
  interactions: seedWidgets.reduce((sum, widget) => sum + widget.interactions, 0),
  leads: seedWidgets.reduce((sum, widget) => sum + (widget.leads || 0), 0),
  appointments: seedWidgets.reduce((sum, widget) => sum + (widget.appointments || 0), 0),
  conversations: seedAgents.reduce((sum, agent) => sum + agent.conversations, 0),
  avgConversion: seedWidgets.length
    ? Number((seedWidgets.reduce((sum, widget) => sum + widget.conversionRate, 0) / seedWidgets.length).toFixed(1))
    : 0,
  salsaBoardSynced: seedWidgets.reduce((sum, widget) => sum + (widget.salsaBoard?.syncedEvents || 0), 0),
  salsaBoardFailed: seedWidgets.reduce((sum, widget) => sum + (widget.salsaBoard?.failedEvents || 0), 0),
}

const defaultState: CustomerJourneyState = {
  agents: seedAgents,
  widgets: seedWidgets,
  selectedAgentId: seedAgents[0].id,
  summary: seedSummary,
}

const emptySummary: CustomerJourneySummary = {
  agents: 0,
  deployments: 0,
  liveWidgets: 0,
  visitors: 0,
  interactions: 0,
  leads: 0,
  appointments: 0,
  conversations: 0,
  avgConversion: 0,
  salsaBoardSynced: 0,
  salsaBoardFailed: 0,
}

const emptyState: CustomerJourneyState = {
  agents: [],
  widgets: [],
  selectedAgentId: null,
  summary: emptySummary,
}

function normalizeJourneyState(input: unknown): CustomerJourneyState {
  if (!input || typeof input !== 'object') {
    return defaultState
  }

  const candidate = input as Partial<CustomerJourneyState>
  const agents = Array.isArray(candidate.agents) ? candidate.agents : defaultState.agents
  const widgets = Array.isArray(candidate.widgets) ? candidate.widgets : defaultState.widgets
  const summary =
    candidate.summary && typeof candidate.summary === 'object'
      ? { ...defaultState.summary, ...(candidate.summary as Partial<CustomerJourneySummary>) }
      : defaultState.summary
  const selectedAgentId =
    typeof candidate.selectedAgentId === 'string' && agents.some((agent) => agent.id === candidate.selectedAgentId)
      ? candidate.selectedAgentId
      : agents[0]?.id ?? null

  return { agents, widgets, selectedAgentId, summary }
}

function requestHeaders(): Record<string, string> {
  return isDemoModeEnabled() ? { 'x-demo-mode': 'true', 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

function compactPayload<T extends Record<string, any>>(payload: T) {
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined))
}

function normalizeDomain(value: unknown) {
  return String(value || '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .toLowerCase()
}

function getRequestedPilotDomain() {
  if (typeof window === 'undefined') return null

  const searchParams = new URLSearchParams(window.location.search)
  const domain = normalizeDomain(searchParams.get('domain'))
  if (domain) return domain

  return searchParams.get('pilot') === 'salsa' ? SALSA_PILOT_DOMAIN : null
}

function isRequestedPilotWidget(widget: CustomerWidget, requestedDomain: string | null) {
  const widgetDomain = normalizeDomain(widget.domain)
  return (
    widget.widgetKey === SALSA_PILOT_WIDGET_KEY ||
    widgetDomain === SALSA_PILOT_DOMAIN ||
    Boolean(requestedDomain && widgetDomain === requestedDomain)
  )
}

function prioritizeRequestedWidget(widgets: CustomerWidget[], requestedDomain: string | null) {
  if (!requestedDomain && !widgets.some((widget) => isRequestedPilotWidget(widget, null))) {
    return widgets
  }

  return [...widgets].sort((a, b) => {
    const aMatch = isRequestedPilotWidget(a, requestedDomain)
    const bMatch = isRequestedPilotWidget(b, requestedDomain)
    if (aMatch === bMatch) return 0
    return aMatch ? -1 : 1
  })
}

function chooseSelectedAgentId(
  agents: CustomerAgent[],
  widgets: CustomerWidget[],
  currentSelectedAgentId: string | null
) {
  const requestedDomain = getRequestedPilotDomain()
  const requestedWidget = widgets.find((widget) => isRequestedPilotWidget(widget, requestedDomain))
  if (requestedWidget && agents.some((agent) => agent.id === requestedWidget.agentId)) {
    return requestedWidget.agentId
  }

  return currentSelectedAgentId && agents.some((agent) => agent.id === currentSelectedAgentId)
    ? currentSelectedAgentId
    : agents[0]?.id ?? null
}

function objectValue(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, any>
    : {}
}

function widgetConfigFor(agent: any) {
  return {
    ...objectValue(agent?.widget_config),
    ...objectValue(agent?.configuration),
  }
}

function normalizeAgentType(value: unknown): CustomerAgent['agent_type'] {
  const allowed: CustomerAgent['agent_type'][] = ['voice_agent', 'text_chatbot', 'custom_llm', 'document_ai', 'api_integration']
  return allowed.includes(value as CustomerAgent['agent_type']) ? value as CustomerAgent['agent_type'] : 'voice_agent'
}

function normalizeLlmProvider(value: unknown): CustomerAgent['llmProvider'] {
  const allowed: CustomerAgent['llmProvider'][] = ['openai', 'groq', 'anthropic', 'custom']
  return allowed.includes(value as CustomerAgent['llmProvider']) ? value as CustomerAgent['llmProvider'] : 'openai'
}

function toApiVoiceModel(voiceModel?: string) {
  const model = (voiceModel || '').trim().toLowerCase()
  if (model.startsWith('cartesia-')) return model.slice('cartesia-'.length)
  if (model.startsWith('elevenlabs-')) return model.slice('elevenlabs-'.length)
  if (model.startsWith('openai-')) return model.slice('openai-'.length)
  if (!model || model === 'default' || model === 'premium neural voice') return 'alloy'
  return model
}

function toApiVoiceProvider(voiceModel?: string) {
  const model = (voiceModel || '').trim().toLowerCase()
  if (model.startsWith('elevenlabs-') || ['rachel', 'josh', 'bella', 'antoni', 'elli', 'adam'].includes(model)) {
    return 'elevenlabs'
  }
  if (model.startsWith('openai-') || ['alloy', 'ash', 'ballad', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer', 'verse'].includes(model)) {
    return 'openai'
  }
  return 'cartesia'
}

function mapRealAgent(agent: any): CustomerAgent {
  const configuration = widgetConfigFor(agent)
  const leads = configuration.leads && typeof configuration.leads === 'object'
    ? configuration.leads
    : { email: true, name: true, phone: false }
  const discover = configuration.discover && typeof configuration.discover === 'object'
    ? configuration.discover
    : { public: false, username: '', title: '', description: '' }

  return {
    id: String(agent.id),
    name: agent.name ?? 'Unnamed Agent',
    description: agent.description ?? '',
    status: agent.is_active === false ? 'inactive' : 'active',
    agent_type: normalizeAgentType(configuration.agentType ?? agent.agent_type),
    conversations: Number(agent.conversation_count ?? agent.conversations ?? 0),
    lastActive: agent.last_active ?? agent.updated_at ?? agent.created_at ?? 'Live',
    businessType: agent.business_type ?? agent.industry ?? 'General',
    voiceModel: configuration.selectedVoice ?? agent.voice_model ?? 'Default',
    widgets: Number(agent.widget_count ?? 0),
    successRate: Number(agent.success_rate ?? 0),
    llmProvider: normalizeLlmProvider(configuration.llmProvider),
    systemPrompt: agent.system_instructions ?? agent.personality_prompt ?? agent.prompt ?? '',
    vibeResponse: configuration.vibeResponse ?? 'friendly',
    leads: {
      email: Boolean(leads.email),
      name: Boolean(leads.name),
      phone: Boolean(leads.phone),
      company: Boolean(leads.company),
      role: Boolean(leads.role),
      useCase: Boolean(leads.useCase),
      timing: Boolean(leads.timing),
      requestedFollowUp: Boolean(leads.requestedFollowUp),
      requireConsent: leads.requireConsent !== false,
      trigger: leads.trigger === 'always' ? 'always' : 'buying_or_demo_intent',
    },
    discover: {
      public: Boolean(discover.public),
      username: discover.username ?? '',
      title: discover.title ?? '',
      description: discover.description ?? '',
    },
  }
}

function mapRealWidget(widget: any, results?: any): CustomerWidget {
  const customization = objectValue(widget.customization) as WidgetCustomization
  const totalSessions = Number(results?.total_sessions ?? widget.total_sessions ?? widget.interactions ?? 0)
  const leadCount = Number(results?.lead_count ?? widget.lead_count ?? widget.leads ?? 0)
  const uniqueSessions = Number(results?.unique_sessions ?? widget.unique_sessions ?? widget.visitors ?? totalSessions)
  const conversionRate = uniqueSessions > 0 ? Number(((leadCount / uniqueSessions) * 100).toFixed(1)) : 0

  return {
    id: String(widget.id),
    agentId: String(widget.agent_id),
    widgetKey: widget.widget_key,
    apiKey: widget.api_key,
    customerId: widget.customer_id,
    customization,
    name: widget.name || `${widget.agent_name || 'Onzoe'} Widget`,
    description: `Managed website widget${widget.domain ? ` for ${widget.domain}` : ''}.`,
    status: widget.is_active === false ? 'inactive' : 'active',
    domain: widget.domain || 'Unassigned domain',
    deploymentDate: String(widget.created_at || new Date().toISOString()).slice(0, 10),
    visitors: uniqueSessions,
    interactions: totalSessions,
    leads: leadCount,
    appointments: Number(results?.appointment_count ?? widget.appointment_count ?? widget.appointments ?? 0),
    conversionRate,
    salsaBoard: {
      syncedEvents: Number(results?.salsa_board?.synced_events ?? widget.salsa_board_synced ?? 0),
      failedEvents: Number(results?.salsa_board?.failed_events ?? widget.salsa_board_failed ?? 0),
      lastSyncedAt: results?.salsa_board?.last_synced_at ?? widget.salsa_board_last_synced_at ?? null,
    },
    deviceBreakdown: { desktop: 0, mobile: 0, tablet: 0 },
    lastUpdated: widget.updated_at || widget.created_at || 'Just now',
    version: 'managed',
  }
}

function summarizeJourney(agents: CustomerAgent[], widgets: CustomerWidget[]): CustomerJourneySummary {
  return {
    agents: agents.length,
    deployments: widgets.length,
    liveWidgets: widgets.filter((widget) => widget.status === 'active').length,
    visitors: widgets.reduce((sum, widget) => sum + widget.visitors, 0),
    interactions: widgets.reduce((sum, widget) => sum + widget.interactions, 0),
    leads: widgets.reduce((sum, widget) => sum + (widget.leads || 0), 0),
    appointments: widgets.reduce((sum, widget) => sum + (widget.appointments || 0), 0),
    conversations: agents.reduce((sum, agent) => sum + agent.conversations, 0),
    avgConversion: widgets.length
      ? Number((widgets.reduce((sum, widget) => sum + widget.conversionRate, 0) / widgets.length).toFixed(1))
      : 0,
    salsaBoardSynced: widgets.reduce((sum, widget) => sum + (widget.salsaBoard?.syncedEvents || 0), 0),
    salsaBoardFailed: widgets.reduce((sum, widget) => sum + (widget.salsaBoard?.failedEvents || 0), 0),
  }
}

function buildScript(widget: CustomerWidget, agent: CustomerAgent) {
  const widgetKey = widget.widgetKey || widget.id
  const customization = widget.customization || {}
  const visualizer = customization.visualizer || {}

  return `<!-- Onzoe Voice AI Widget -->
<script>
  window.onzoeWidgetConfig = {
    widgetKey: '${widgetKey}',
    apiBase: 'https://onzoe.com/api',
    agentName: '${agent.name}',
    domain: '${widget.domain}',
    position: 'bottom-right',
    greetingMessage: '${customization.welcomeMessage || customization.greetingMessage || 'Hi! How can I help you today?'}',
    primaryColor: '${customization.primaryColor || '#3B82F6'}',
    analytics: { enabled: true },
    leadCapture: { enabled: true, email: true, name: true, phone: false },
    visualizer: {
      enabled: true,
      type: '${visualizer.type || 'waveform'}',
      theme: '${visualizer.theme || 'blue'}',
      size: '${visualizer.size || 'medium'}',
      showInChat: true,
      showInBubble: false
    }
  };
</script>
<script src="https://onzoe.com/widget/widget-loader.js?key=${encodeURIComponent(widgetKey)}" async></script>`
}

const CustomerJourneyContext = createContext<CustomerJourneyContextType | undefined>(undefined)

export const CustomerJourneyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [state, setState] = useState<CustomerJourneyState>(() => {
    if (typeof window === 'undefined') {
      return emptyState
    }

    if (!isDemoModeEnabled()) {
      return emptyState
    }

    try {
      const saved = window.localStorage.getItem(STORAGE_KEY)
      return saved ? normalizeJourneyState(JSON.parse(saved)) : defaultState
    } catch {
      return defaultState
    }
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    }
  }, [state])

  useEffect(() => {
    let cancelled = false

    const loadJourney = async () => {
      if (authLoading) return
      const demoMode = isDemoModeEnabled()
      if (!isAuthenticated && !demoMode) {
        setState(emptyState)
        return
      }

      if (demoMode) {
        setState((current) => ({
          ...current,
          summary: summarizeJourney(current.agents, current.widgets),
          selectedAgentId:
            current.selectedAgentId && current.agents.some((agent) => agent.id === current.selectedAgentId)
              ? current.selectedAgentId
              : current.agents[0]?.id ?? null,
        }))
        return
      }

      if (isAuthenticated) {
        try {
          const [agentsResponse, widgetsResponse] = await Promise.all([
            fetch('/api/agents', { credentials: 'include' }),
            fetch('/api/widgets', { credentials: 'include' }),
          ])

          if (agentsResponse.ok && widgetsResponse.ok) {
            const [agentsPayload, widgetsPayload] = await Promise.all([
              agentsResponse.json(),
              widgetsResponse.json(),
            ])

            if (agentsPayload?.success && widgetsPayload?.success && !cancelled) {
              const realAgents = Array.isArray(agentsPayload.agents)
                ? agentsPayload.agents.map(mapRealAgent)
                : []

              const rawWidgets = Array.isArray(widgetsPayload.widgets) ? widgetsPayload.widgets : []
              const widgetResults = await Promise.all(
                rawWidgets.map(async (widget: any) => {
                  try {
                    const response = await fetch(`/api/widgets/${widget.id}/results`, {
                      credentials: 'include',
                    })
                    if (!response.ok) return null
                    const payload = await response.json()
                    return payload?.success ? payload.results : null
                  } catch {
                    return null
                  }
                })
              )

              const realWidgets = rawWidgets.map((widget: any, index: number) =>
                mapRealWidget(widget, widgetResults[index])
              )
              const prioritizedWidgets = prioritizeRequestedWidget(realWidgets, getRequestedPilotDomain())

              setState((current) => ({
                agents: realAgents,
                widgets: prioritizedWidgets,
                summary: summarizeJourney(realAgents, prioritizedWidgets),
                selectedAgentId: chooseSelectedAgentId(realAgents, prioritizedWidgets, current.selectedAgentId),
              }))
              return
            }
          }

          if (!cancelled) {
            setState(emptyState)
          }
        } catch (error) {
          console.warn('Real marketing APIs unavailable.', error)
          if (!cancelled) {
            setState(emptyState)
          }
        }
      }

      if (!isDemoModeEnabled()) return

      try {
        const response = await fetch('/api/review-workspace/customer-journey', {
          credentials: 'include',
          headers: isDemoModeEnabled() ? { 'x-demo-mode': 'true' } : undefined,
        })

        if (!response.ok) return

        const payload = await response.json()
        if (!payload?.success || cancelled) return

        setState((current) => {
          const nextAgents = Array.isArray(payload.agents) ? payload.agents : current.agents
          const nextWidgets = Array.isArray(payload.widgets) ? payload.widgets : current.widgets

          return {
            agents: nextAgents,
            widgets: nextWidgets,
            summary: payload.summary ?? summarizeJourney(nextAgents, nextWidgets),
            selectedAgentId:
              current.selectedAgentId && nextAgents.some((agent: CustomerAgent) => agent.id === current.selectedAgentId)
                ? current.selectedAgentId
                : nextAgents[0]?.id ?? null,
          }
        })
      } catch (error) {
        console.warn('Customer journey BFF unavailable, using local state.', error)
      }
    }

    loadJourney()

    return () => {
      cancelled = true
    }
  }, [authLoading, isAuthenticated])

  const selectedAgent = useMemo(
    () => state.agents.find((agent) => agent.id === state.selectedAgentId) ?? state.agents[0] ?? null,
    [state.agents, state.selectedAgentId]
  )

  const selectedWidgets = useMemo(
    () => state.widgets.filter((widget) => widget.agentId === selectedAgent?.id),
    [state.widgets, selectedAgent]
  )

  const selectAgent = (agentId: string) => {
    setState((current) => ({ ...current, selectedAgentId: agentId }))
  }

  const createAgent = async (agentData: AgentFormData) => {
    const newAgent: CustomerAgent = {
      id: `agent_${Date.now()}`,
      name: agentData.name,
      description: agentData.description,
      status: 'training',
      agent_type: agentData.agentType,
      conversations: 0,
      lastActive: 'Just now',
      businessType: agentData.businessType,
      voiceModel: agentData.voiceModel || (agentData.agentType === 'voice_agent' ? 'Premium Neural Voice' : 'Text Only'),
      widgets: 0,
      successRate: 0,
      llmProvider: agentData.llmProvider,
      systemPrompt: agentData.systemPrompt,
      vibeResponse: 'friendly',
      leads: { email: true, name: true, phone: false },
      discover: {
        public: false,
        username: '',
        title: '',
        description: '',
      },
    }

    setState((current) => ({
      ...current,
      agents: [newAgent, ...current.agents],
      summary: {
        ...current.summary,
        agents: current.summary.agents + 1,
      },
      selectedAgentId: newAgent.id,
    }))

    try {
      const createPayload = {
        name: agentData.name,
        description: agentData.description,
        personality_prompt: agentData.systemPrompt,
        system_instructions: agentData.systemPrompt,
        voice_model: toApiVoiceModel(agentData.voiceModel),
        voice_provider: toApiVoiceProvider(agentData.voiceModel),
        business_type: agentData.businessType,
        widget_config: {
          llmProvider: agentData.llmProvider,
          agentType: agentData.agentType,
          selectedVoice: agentData.voiceModel,
          vibeResponse: 'friendly',
          leads: { email: true, name: true, phone: false },
          discover: { public: false, username: '', title: '', description: '' },
        },
      }

      const response = await api.post('/api/agents', createPayload)
      if (response.data?.success && response.data.agent) {
        const createdAgent = mapRealAgent(response.data.agent)
        setState((current) => ({
          ...current,
          agents: [createdAgent, ...current.agents.filter((agent) => agent.id !== newAgent.id)],
          summary: summarizeJourney([createdAgent, ...current.agents.filter((agent) => agent.id !== newAgent.id)], current.widgets),
          selectedAgentId: createdAgent.id,
        }))
        return createdAgent
      }
    } catch (error) {
      console.warn('Real agent API unavailable.', error)
      if (!isDemoModeEnabled()) {
        setState((current) => ({
          ...current,
          agents: current.agents.filter((agent) => agent.id !== newAgent.id),
          summary: summarizeJourney(current.agents.filter((agent) => agent.id !== newAgent.id), current.widgets),
          selectedAgentId: current.agents.find((agent) => agent.id !== newAgent.id)?.id ?? null,
        }))
        throw error instanceof Error ? error : new Error('Failed to create agent')
      }
    }

    if (isDemoModeEnabled()) {
      return newAgent
    }

    try {
      const response = await fetch('/api/review-workspace/agents', {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders(),
        body: JSON.stringify(agentData),
      })

      if (!response.ok) {
        throw new Error('Failed to create agent')
      }

      const payload = await response.json()
      if (!payload?.success || !payload.agent) {
        throw new Error('Invalid create-agent response')
      }

      const createdAgent = payload.agent as CustomerAgent
      setState((current) => ({
        ...current,
        agents: [createdAgent, ...current.agents.filter((agent) => agent.id !== newAgent.id)],
        summary: {
          ...current.summary,
          agents: current.agents.filter((agent) => agent.id !== newAgent.id).length + 1,
          conversations: [createdAgent, ...current.agents.filter((agent) => agent.id !== newAgent.id)]
            .reduce((sum, agent) => sum + agent.conversations, 0),
        },
        selectedAgentId: createdAgent.id,
      }))

      return createdAgent
    } catch (error) {
      console.warn('Create agent BFF unavailable, keeping optimistic local state.', error)
      return newAgent
    }
  }

  const updateAgent = async (agentId: string, updates: Partial<CustomerAgent>) => {
    const previousAgents = state.agents

    setState((current) => ({
      ...current,
      agents: current.agents.map((agent) => (agent.id === agentId ? { ...agent, ...updates } : agent)),
      summary: {
        ...current.summary,
        conversations: current.agents
          .map((agent) => (agent.id === agentId ? { ...agent, ...updates } : agent))
          .reduce((sum, agent) => sum + agent.conversations, 0),
      },
    }))

    if (isDemoModeEnabled()) {
      return
    }

    try {
      const currentAgent = previousAgents.find((agent) => agent.id === agentId)
      const response = await api.put(`/api/agents/${agentId}`, compactPayload({
        name: updates.name,
        description: updates.description,
        personality_prompt: updates.systemPrompt,
        system_instructions: updates.systemPrompt,
        voice_model: updates.voiceModel ? toApiVoiceModel(updates.voiceModel) : undefined,
        voice_provider: updates.voiceModel ? toApiVoiceProvider(updates.voiceModel) : undefined,
        business_type: updates.businessType,
        widget_config: {
          llmProvider: updates.llmProvider ?? currentAgent?.llmProvider ?? 'openai',
          agentType: updates.agent_type ?? currentAgent?.agent_type ?? 'voice_agent',
          selectedVoice: updates.voiceModel ?? currentAgent?.voiceModel,
          vibeResponse: updates.vibeResponse ?? currentAgent?.vibeResponse ?? 'friendly',
          leads: updates.leads ?? currentAgent?.leads ?? { email: true, name: true, phone: false },
          discover: updates.discover ?? currentAgent?.discover ?? { public: false, username: '', title: '', description: '' },
        },
      }))

      if (response.data?.success && response.data.agent) {
        const updatedAgent = {
          ...mapRealAgent(response.data.agent),
          leads: updates.leads ?? currentAgent?.leads ?? mapRealAgent(response.data.agent).leads,
          discover: updates.discover ?? currentAgent?.discover ?? mapRealAgent(response.data.agent).discover,
          vibeResponse: updates.vibeResponse ?? currentAgent?.vibeResponse ?? mapRealAgent(response.data.agent).vibeResponse,
        }

        setState((current) => ({
          ...current,
          agents: current.agents.map((agent) => (agent.id === agentId ? updatedAgent : agent)),
          summary: summarizeJourney(current.agents.map((agent) => (agent.id === agentId ? updatedAgent : agent)), current.widgets),
        }))
        return
      }
    } catch (error) {
      console.warn('Real agent update API unavailable.', error)
      if (!isDemoModeEnabled()) {
        setState((current) => ({ ...current, agents: previousAgents }))
        throw error instanceof Error ? error : new Error('Failed to update agent')
      }
    }

    try {
      const response = await fetch(`/api/review-workspace/agents/${agentId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: requestHeaders(),
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        throw new Error('Failed to update agent')
      }

      const payload = await response.json()
      if (payload?.success && payload.agent) {
        const updatedAgent = payload.agent as CustomerAgent
        setState((current) => ({
          ...current,
          agents: current.agents.map((agent) => (agent.id === agentId ? { ...agent, ...updatedAgent } : agent)),
          summary: {
            ...current.summary,
            conversations: current.agents
              .map((agent) => (agent.id === agentId ? { ...agent, ...updatedAgent } : agent))
              .reduce((sum, agent) => sum + agent.conversations, 0),
          },
        }))
      }
    } catch (error) {
      console.warn('Update agent BFF unavailable, restoring previous local state.', error)
      setState((current) => ({ ...current, agents: previousAgents }))
    }
  }

  const deleteAgent = async (agentId: string) => {
    const previousState = state

    setState((current) => {
      const nextAgents = current.agents.filter((agent) => agent.id !== agentId)
      const nextWidgets = current.widgets.filter((widget) => widget.agentId !== agentId)
      return {
        ...current,
        agents: nextAgents,
        widgets: nextWidgets,
        summary: {
          ...current.summary,
          agents: nextAgents.length,
          deployments: nextWidgets.length,
          liveWidgets: nextWidgets.filter((widget) => widget.status === 'active').length,
          visitors: nextWidgets.reduce((sum, widget) => sum + widget.visitors, 0),
          interactions: nextWidgets.reduce((sum, widget) => sum + widget.interactions, 0),
          leads: nextWidgets.reduce((sum, widget) => sum + (widget.leads || 0), 0),
          appointments: nextWidgets.reduce((sum, widget) => sum + (widget.appointments || 0), 0),
          conversations: nextAgents.reduce((sum, agent) => sum + agent.conversations, 0),
          avgConversion: nextWidgets.length
            ? Number((nextWidgets.reduce((sum, widget) => sum + widget.conversionRate, 0) / nextWidgets.length).toFixed(1))
            : 0,
        },
        selectedAgentId: nextAgents[0]?.id ?? null,
      }
    })

    if (isDemoModeEnabled()) {
      return
    }

    try {
      await api.delete(`/api/agents/${agentId}`)
    } catch (error) {
      console.warn('Delete agent API unavailable, restoring previous local state.', error)
      setState(previousState)
      throw error instanceof Error ? error : new Error('Failed to delete agent')
    }
  }

  const ensureWidgetForAgent = async (agentId: string, domain?: string) => {
    const existing = state.widgets.find((widget) => widget.agentId === agentId)
    if (existing) {
      setState((current) => ({ ...current, selectedAgentId: agentId }))
      return existing
    }

    const agent = state.agents.find((item) => item.id === agentId)
    const widget: CustomerWidget = {
      id: `widget_${Date.now()}`,
      agentId,
      name: `${agent?.name || 'Onzoe Agent'} Widget`,
      description: 'Embeddable website widget generated from the current agent configuration.',
      status: 'pending',
      domain: domain || 'yourdomain.com',
      customization: {
        welcomeMessage: `Hi! You're speaking with ${agent?.name || 'your Onzoe assistant'}.`,
        primaryColor: '#0ea5e9',
        voiceExperience: {
          interactionMode: 'hands_free',
          bargeIn: true,
        },
        visualizer: {
          enabled: true,
          type: 'waveform',
          theme: 'blue',
          size: 'medium',
        },
      },
      deploymentDate: new Date().toISOString().slice(0, 10),
      visitors: 0,
      interactions: 0,
      leads: 0,
      conversionRate: 0,
      deviceBreakdown: { desktop: 0, mobile: 0, tablet: 0 },
      lastUpdated: 'Just now',
      version: 'v1.0.0',
    }

    setState((current) => ({
      ...current,
      widgets: [widget, ...current.widgets],
      agents: current.agents.map((item) => (
        item.id === agentId ? { ...item, widgets: item.widgets + 1, status: item.status === 'training' ? 'active' : item.status } : item
      )),
      summary: {
        ...current.summary,
        deployments: current.summary.deployments + 1,
      },
      selectedAgentId: agentId,
    }))

    if (isDemoModeEnabled()) {
      return widget
    }

    try {
      const response = await api.post(`/api/agents/${encodeURIComponent(agentId)}/widgets`, {
        domain: domain || 'yourdomain.com',
        allowed_origins: [domain || 'yourdomain.com'],
        customization: {
          welcomeMessage: `Hi! You're speaking with ${agent?.name || 'your Onzoe assistant'}.`,
          primaryColor: '#0ea5e9',
          visualizer: {
            enabled: true,
            type: 'waveform',
            theme: 'blue',
            size: 'medium',
          },
        },
      })

      if (response.data?.success && response.data.widget) {
        const createdWidget = mapRealWidget(response.data.widget)
        setState((current) => ({
          ...current,
          widgets: [createdWidget, ...current.widgets.filter((item) => item.id !== widget.id)],
          agents: current.agents.map((item) => (
            item.id === agentId ? { ...item, widgets: Math.max(item.widgets, 1), status: item.status === 'training' ? 'active' : item.status } : item
          )),
          summary: summarizeJourney(
            current.agents.map((item) => (
              item.id === agentId ? { ...item, widgets: Math.max(item.widgets, 1), status: item.status === 'training' ? 'active' : item.status } : item
            )),
            [createdWidget, ...current.widgets.filter((item) => item.id !== widget.id)]
          ),
          selectedAgentId: agentId,
        }))
        return createdWidget
      }
    } catch (error) {
      console.warn('Real widget API unavailable.', error)
      if (!isDemoModeEnabled()) {
        setState((current) => ({
          ...current,
          widgets: current.widgets.filter((item) => item.id !== widget.id),
          agents: current.agents.map((item) => (
            item.id === agentId ? { ...item, widgets: Math.max(0, item.widgets - 1) } : item
          )),
          summary: summarizeJourney(
            current.agents.map((item) => (
              item.id === agentId ? { ...item, widgets: Math.max(0, item.widgets - 1) } : item
            )),
            current.widgets.filter((item) => item.id !== widget.id)
          ),
        }))
        throw error instanceof Error ? error : new Error('Failed to create widget')
      }
    }

    try {
      const response = await fetch(`/api/review-workspace/agents/${agentId}/widgets`, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders(),
        body: JSON.stringify({
          name: widget.name,
          description: widget.description,
          domain: widget.domain,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to create widget')
      }

      const payload = await response.json()
      if (!payload?.success || !payload.widget) {
        throw new Error('Invalid create-widget response')
      }

      const createdWidget = payload.widget as CustomerWidget
      setState((current) => ({
        ...current,
        widgets: [createdWidget, ...current.widgets.filter((item) => item.id !== widget.id)],
        agents: current.agents.map((item) => (
          item.id === agentId ? { ...item, widgets: Math.max(item.widgets, 1), status: item.status === 'training' ? 'active' : item.status } : item
        )),
        summary: {
          ...current.summary,
          deployments: current.summary.deployments + (current.widgets.some((item) => item.id === widget.id) ? 0 : 1),
          liveWidgets:
            createdWidget.status === 'active'
              ? current.summary.liveWidgets + 1
              : current.summary.liveWidgets,
          visitors: current.widgets
            .filter((item) => item.id !== widget.id)
            .reduce((sum, item) => sum + item.visitors, 0) + createdWidget.visitors,
          interactions: current.widgets
            .filter((item) => item.id !== widget.id)
            .reduce((sum, item) => sum + item.interactions, 0) + createdWidget.interactions,
          leads: current.widgets
            .filter((item) => item.id !== widget.id)
            .reduce((sum, item) => sum + (item.leads || 0), 0) + (createdWidget.leads || 0),
          appointments: current.widgets
            .filter((item) => item.id !== widget.id)
            .reduce((sum, item) => sum + (item.appointments || 0), 0) + (createdWidget.appointments || 0),
          avgConversion: (() => {
            const nextWidgets = [createdWidget, ...current.widgets.filter((item) => item.id !== widget.id)]
            return nextWidgets.length
              ? Number((nextWidgets.reduce((sum, item) => sum + item.conversionRate, 0) / nextWidgets.length).toFixed(1))
              : 0
          })(),
        },
        selectedAgentId: agentId,
      }))

      return createdWidget
    } catch (error) {
      console.warn('Create widget BFF unavailable, keeping optimistic local state.', error)
      return widget
    }
  }

  const getInstallScript = (widgetId: string) => {
    const widget = state.widgets.find((item) => item.id === widgetId)
    const agent = state.agents.find((item) => item.id === widget?.agentId)
    if (!widget || !agent) {
      return ''
    }

    return buildScript(widget, agent)
  }

  const updateWidget = async (widgetId: string, updates: Partial<CustomerWidget>) => {
    const previousState = state
    const existingWidget = state.widgets.find((item) => item.id === widgetId)

    if (!existingWidget) {
      throw new Error('Widget not found')
    }

    const optimisticWidget: CustomerWidget = {
      ...existingWidget,
      ...updates,
      customization: {
        ...(existingWidget.customization || {}),
        ...(updates.customization || {}),
      },
      lastUpdated: new Date().toISOString(),
    }

    setState((current) => ({
      ...current,
      widgets: current.widgets.map((item) => (item.id === widgetId ? optimisticWidget : item)),
      summary: summarizeJourney(
        current.agents,
        current.widgets.map((item) => (item.id === widgetId ? optimisticWidget : item))
      ),
    }))

    if (isDemoModeEnabled()) {
      return optimisticWidget
    }

    try {
      const response = await api.put(`/api/widgets/${encodeURIComponent(widgetId)}`, compactPayload({
        domain: updates.domain,
        customization: optimisticWidget.customization,
        is_active: updates.status === 'active'
          ? true
          : updates.status === 'inactive'
            ? false
            : undefined,
      }))

      if (!response.data?.success || !response.data.widget) {
        throw new Error(response.data?.message || 'Failed to update widget')
      }

      const mappedWidget = mapRealWidget({
        ...response.data.widget,
        agent_name: state.agents.find((agent) => agent.id === existingWidget.agentId)?.name,
      })

      setState((current) => ({
        ...current,
        widgets: current.widgets.map((item) => (item.id === widgetId ? mappedWidget : item)),
        summary: summarizeJourney(
          current.agents,
          current.widgets.map((item) => (item.id === widgetId ? mappedWidget : item))
        ),
      }))

      return mappedWidget
    } catch (error) {
      console.warn('Widget update API unavailable, restoring previous widget state.', error)
      setState(previousState)
      throw error instanceof Error ? error : new Error('Failed to update widget')
    }
  }

  return (
    <CustomerJourneyContext.Provider
      value={{
        agents: state.agents,
        widgets: state.widgets,
        summary: state.summary,
        selectedAgent,
        selectedAgentId: state.selectedAgentId,
        selectedWidgets,
        selectAgent,
        createAgent,
        updateAgent,
        deleteAgent,
        ensureWidgetForAgent,
        updateWidget,
        getInstallScript,
      }}
    >
      {children}
    </CustomerJourneyContext.Provider>
  )
}

export const useCustomerJourney = () => {
  const context = useContext(CustomerJourneyContext)
  if (!context) {
    throw new Error('useCustomerJourney must be used within a CustomerJourneyProvider')
  }
  return context
}
